import { computed, Injectable, signal } from '@angular/core';
import {
  ALL_TFA_SHARE_PERMISSIONS,
  hasAnyTfaSharePermission,
  type TfaSharePermissions,
} from '@sportello-balneari/api-client';

/**
 * Which sections of the answer on screen the current user may change.
 *
 * This is the *rights* side of read-only rendering — what the backend granted
 * this user on this answer — as opposed to `EVALUATION_CAPABILITIES`, which is
 * the *policy* of the rendering (the demo withholds downloads whoever you
 * are). Defaults to everything, the owner's view, so a page that never sets it
 * behaves as before; the host page sets it from `collaboratorPermissions` once
 * the answer is loaded, and each section then renders itself editable or not
 * by asking here: the applicant documents ask `administrativeOffer`, the form,
 * its attachments, the premessa and the compiler `technicalOffer`, the PEF,
 * the economic offer and the PEF report `economicOffer`; what spans the whole
 * answer (save, export, requesting the evaluation) asks `canEditAny`.
 *
 * Component-scoped through `provideCompiling()`, like the rest of the answer
 * state. These hide UI; the backend (`TenderFormAnswerAccess`) refuses.
 */
@Injectable()
export class AnswerPermissionsService {
  readonly permissions = signal<TfaSharePermissions>(ALL_TFA_SHARE_PERMISSIONS);

  readonly canEditAdministrativeOffer = computed(
    () => this.permissions().administrativeOffer,
  );
  readonly canEditTechnicalOffer = computed(
    () => this.permissions().technicalOffer,
  );
  readonly canEditEconomicOffer = computed(
    () => this.permissions().economicOffer,
  );

  /** False is sola lettura: nothing on the page writes. */
  readonly canEditAny = computed(() =>
    hasAnyTfaSharePermission(this.permissions()),
  );

  /** `null` (the caller's own answer) means everything. */
  set(permissions: TfaSharePermissions | null | undefined): void {
    this.permissions.set(permissions ?? ALL_TFA_SHARE_PERMISSIONS);
  }
}
